import { IErrorResponse } from '@common/api.models';
import { IProjectFileDownloadRequest, IFileResponse, EProjectFileCategory } from '@file/projectFile/projectFile.models';
import { downloadFile } from '@file/projectFile/projectFile.services';
import { CircularProgressBar, Grid } from '@gcl/react-core';
import { AxiosError } from 'axios';
import * as React from 'react';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { apiErrorHandle } from '@core/error.services';
import { downloadProjectFileAsync } from './projectFile/projectFile.services';
import './DownloadFile.scss';

type DownloadFileRenderFn = (props: {
  useMutationResult: UseMutationResult<
    IFileResponse,
    AxiosError<IErrorResponse, any>,
    IProjectFileDownloadRequest,
    unknown
  >;
  onClickDownloadFile: () => void;
  progress: number;
}) => React.ReactElement;

export interface IDownloadFileProps {
  text?: string | undefined;
  onError?: (errors: { [key: string]: string } | undefined) => void;
  projectId: number;
  attachmentId: number;
  shouldDownloadOnSuccess?: boolean;
  render?: DownloadFileRenderFn;
  type?: EProjectFileCategory;
  shouldNotChop?: boolean;
  shouldChopICEApprovalOnly?: boolean;
}

export const DownloadFile: React.FC<IDownloadFileProps> = ({
  text,
  onError,
  projectId,
  attachmentId,
  render,
  shouldDownloadOnSuccess = true,
  type = EProjectFileCategory.drawings,
  shouldNotChop = false,
  shouldChopICEApprovalOnly = false,
}) => {
  const [progress, setProgress] = React.useState(0);
  const useMutationResult = useMutation<IFileResponse, any, IProjectFileDownloadRequest, any>(
    downloadProjectFileAsync,
    {
      onError: (error: AxiosError<IErrorResponse>) => {
        setProgress(0);
        const { formErrors: errors, errorMessage, errorType } = apiErrorHandle(error, 'Failed to download file');
        if (onError && typeof onError === 'function') {
          onError(errors);
        }
      },
      onSuccess: (data, variables, context) => {
        setProgress(0);
        if (shouldDownloadOnSuccess) downloadFile(data.file, data.filename);
      },
    },
  );
  const { mutate, isLoading, error, reset } = useMutationResult;
  const onClickDownloadFile = () => {
    mutate({
      projectId,
      projectFileId: attachmentId,
      type,
      shouldNotChop,
      shouldChopICEApprovalOnly,
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setProgress(percentCompleted);
      },
    });
  };
  return (
    <>
      <ApiErrorDisplay errors={[{ error, genericError: 'Failed to download file', reset }]} />
      {render && typeof render === 'function' ? (
        render({ useMutationResult, onClickDownloadFile, progress })
      ) : (
        <Grid container alignItems="center" flexWrap="nowrap">
          <Grid
            item
            className="gcl-clickable gcl-fl-md"
            onClick={() => {
              onClickDownloadFile();
            }}
          >
            {text}
          </Grid>

          <Grid
            item
            flex="0 1 30px"
            className="gcl-pl-xs gcl-gems-download-loading"
            sx={[isLoading && { visibility: 'visible' }, !isLoading && { display: 'none' }]}
          >
            <CircularProgressBar className="gcl-gems-download-loading" value={progress} />
          </Grid>
        </Grid>
      )}
    </>
  );
};
